import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { ShareIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useIsChatLocked } from '../../../../libs/hooks/useIsChatLocked';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { setSelectedTab } from '../../../../redux/features/app/appSlice';
import { AppTabs, FeatureKeys } from '../../../../redux/features/app/AppState';
import { ShareChatDialog } from '../Dialogs/ShareChatDialog';
import styles from './ChatWindow.module.scss';
import { EditChatScope } from './Shared/EditChatScope';
import { ChatTab } from './Tabs/ChatTab';
import { DocumentsTab } from './Tabs/DocumentsTab';
import { SitesTab } from './Tabs/SitesTab';
import { Alerts } from './Shared/Alerts';

const tabStyles = {
    tab: `border-transparent border-b-2 py-3 px-1.5 mr-1 sm:px-2 sm:mr-2 hover:border-slate-400 dark-hover:border-slate-400 transition-all duration-200 whitespace-nowrap`,
    selectedTab: `!border-primary-300`,
};

interface IChatWindowProps {
    chatMode: string;
}

export const ChatWindow: React.FC<IChatWindowProps> = ({ chatMode }) => {
    const dispatch = useAppDispatch();
    const { features, selectedTab } = useAppSelector((state: RootState) => state.app);
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { isLockedChat, lockReason } = useIsChatLocked(conversations, selectedId);
    const [shareChatDialogOpen, setShareChatDialogOpen] = useState(false);
    const { serviceInfo } = useAppSelector((state: RootState) => state.app);
    const threadId = conversations[selectedId]?.threadId ?? '';
    const chatDisabled = !serviceInfo.isInsightFeatureEnabled && threadId;

    React.useEffect(() => {
        if (typeof conversations[selectedId] !== 'undefined') {
            const chatScope = conversations[selectedId].chatScope;
            if (
                selectedTab === AppTabs.Sites &&
                (!chatScope?.startsWith('SPO:') || (chatScope.startsWith('SPO:') && chatScope.split(':').length > 2))
            ) {
                dispatch(setSelectedTab(AppTabs.Chat));
            }
        }
    }, [conversations, selectedId, selectedTab, dispatch]);

    let chatScope: string | undefined = undefined;

    if (selectedId && conversations[selectedId]?.chatScope) {
        chatScope = conversations[selectedId].chatScope;
    }

    if (!selectedId) {
        return (
            <div className={styles.container}>
                <main className="grid min-h-full place-items-center px-6 py-24 sm:py-32 lg:px-8">
                    <div className="text-center">
                        <p className="mt-6 text-pretty text-lg font-medium sm:text-xl/8">
                            <InformationCircleIcon className="h-6 w-6 inline-block mr-1 align-sub" />
                            No conversations selected
                        </p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className="flex w-[100%] flex-col overflow-y">
                <div className="w-full items-end border-t border-slate-300 bg-slate-300 px-2 py-0 dark:border-slate-700 dark:bg-slate-700 flex flex-row justify-between">
                    <div className="flex">
                        <a
                            href="#"
                            onClick={() => dispatch(setSelectedTab(AppTabs.Chat))}
                            className={`${tabStyles.tab} ${selectedTab === AppTabs.Chat ? tabStyles.selectedTab : ''}`}>
                            Chat
                        </a>
                        {!isLockedChat && !chatDisabled && (
                            <a
                                href="#"
                                onClick={() => dispatch(setSelectedTab(AppTabs.Documents))}
                                className={`${tabStyles.tab} ${selectedTab === AppTabs.Documents ? tabStyles.selectedTab : ''}`}>
                                Documents
                            </a>
                        )}
                        {!isLockedChat && chatScope?.startsWith('SPO:') && chatScope.split(':').length === 2 && (
                            <a
                                href="#"
                                onClick={() => dispatch(setSelectedTab(AppTabs.Sites))}
                                className={`${tabStyles.tab} ${selectedTab === AppTabs.Sites ? tabStyles.selectedTab : ''}`}>
                                Indexed sources
                            </a>
                        )}
                    </div>
                    <div className="flex items-center my-2">
                        {features[FeatureKeys.ShowPrompt].enabled && !isLockedChat && (
                            <EditChatScope chatId={selectedId} chatScope={chatScope} />
                        )}
                        {features[FeatureKeys.MultiUserChat].enabled &&
                            conversations[selectedId].isSaved &&
                            !isLockedChat &&
                            shareChatDialogOpen && (
                                <>
                                    <ShareChatDialog
                                        chatId={selectedId}
                                        open={shareChatDialogOpen}
                                        onClose={() => setShareChatDialogOpen(false)}
                                    />
                                    <a
                                        href="#"
                                        title="Share chat"
                                        className="hidden sm:block rounded-lg p-1 sm:p-1.5 mx-1 text-slate-500 hover:bg-base-50 group px-2 hover:text-primary-300 text-base-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800"
                                        onClick={() => setShareChatDialogOpen(true)}>
                                        <ShareIcon className="h-5 w-5" strokeWidth={2} />
                                    </a>
                                </>
                            )}
                    </div>
                </div>
                {isLockedChat && (
                    <div
                        className="p-4 mb-4 text-sm text-yellow-900 bg-yellow-100 dark:bg-yellow-100 dark:text-yellow-900"
                        role="alert">
                        {`This chat is now read-only due to a recent system upgrade. You can read the conversation, but any new messages need to be in a new chat.${lockReason ? ` Reason: ${lockReason}` : ''}`}
                    </div>
                )}
                {selectedTab === AppTabs.Chat ? (
                    <ChatTab chatMode={chatMode} />
                ) : selectedTab === AppTabs.Documents ? (
                    <DocumentsTab />
                ) : selectedTab === AppTabs.Sites ? (
                    <SitesTab />
                ) : null}
                <div>
                    <Alerts />
                </div>
            </div>
        </div>
    );
};
